/**
 * Types et helpers pour la gestion des features et niveaux
 */

export type FeatureLevel = 'basic' | 'standard' | 'advanced' | 'limited' | 'unlimited' | 'retention_30' | boolean;

export type FeatureName =
  | 'multicloud'
  | 'export_terraform'
  | 'export_bicep'
  | 'cost_estimation'
  | 'monitoring'
  | 'finops_advanced'
  | 'sso'
  | 'audit_logs'
  | 'api_access'
  | 'white_label'
  | 'dedicated_support'
  | 'priority_support'
  | 'byoo_azure_devops'
  | 'multi_tenant_clients'
  | 'sla'
  | 'custom_integrations';

export interface Feature {
  name: FeatureName;
  level: FeatureLevel;
  available: boolean;
  displayName: string;
  description?: string;
}

export interface FeatureAccessResult {
  allowed: boolean;
  current_level: FeatureLevel | null;
  required_level?: FeatureLevel;
  reason?: string;
  upgrade_suggestion?: {
    target_plan: string;
    feature_level: FeatureLevel;
    message: string;
  };
}

/**
 * Obtenir le badge de couleur pour un niveau de feature
 */
export function getFeatureBadgeColor(level: FeatureLevel): string {
  if (typeof level === 'boolean') {
    return level ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600';
  }

  switch (level) {
    case 'basic':
      return 'bg-blue-100 text-blue-800';
    case 'standard':
      return 'bg-green-100 text-green-800';
    case 'advanced':
      return 'bg-purple-100 text-purple-800';
    case 'limited':
      return 'bg-yellow-100 text-yellow-800';
    case 'unlimited':
      return 'bg-indigo-100 text-indigo-800';
    case 'retention_30':
      return 'bg-teal-100 text-teal-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

/**
 * Obtenir l'icône pour un niveau de feature
 */
export function getFeatureIcon(level: FeatureLevel): string {
  if (typeof level === 'boolean') {
    return level ? '✓' : '✗';
  }

  switch (level) {
    case 'basic':
      return '○';
    case 'standard':
      return '●';
    case 'advanced':
      return '★';
    case 'limited':
      return '◐';
    case 'unlimited':
      return '∞';
    case 'retention_30':
      return '30d';
    default:
      return '?';
  }
}

/**
 * Obtenir le label lisible d'un niveau
 */
export function getFeatureLevelLabel(level: FeatureLevel): string {
  if (typeof level === 'boolean') {
    return level ? 'Disponible' : 'Non disponible';
  }

  switch (level) {
    case 'basic':
      return 'Basique';
    case 'standard':
      return 'Standard';
    case 'advanced':
      return 'Avancé';
    case 'limited':
      return 'Limité';
    case 'unlimited':
      return 'Illimité';
    case 'retention_30':
      return '30 jours';
    default:
      return level;
  }
}

/**
 * Comparer deux niveaux de features
 * @returns nombre positif si level1 > level2, négatif si level1 < level2, 0 si égaux
 */
export function compareFeatureLevels(level1: FeatureLevel, level2: FeatureLevel): number {
  const levels: Record<string, number> = {
    basic: 1,
    standard: 2,
    advanced: 3,
    limited: 2,
    unlimited: 3,
    retention_30: 2
  };

  const value1 = typeof level1 === 'boolean' ? (level1 ? 1 : 0) : (levels[level1] || 0);
  const value2 = typeof level2 === 'boolean' ? (level2 ? 1 : 0) : (levels[level2] || 0);

  return value1 - value2;
}

/**
 * Vérifier si un niveau de feature est suffisant
 */
export function isFeatureLevelSufficient(currentLevel: FeatureLevel, requiredLevel: FeatureLevel): boolean {
  return compareFeatureLevels(currentLevel, requiredLevel) >= 0;
}

/**
 * Obtenir les noms de features lisibles
 */
export const FEATURE_DISPLAY_NAMES: Record<FeatureName, string> = {
  multicloud: 'Multi-cloud',
  export_terraform: 'Export Terraform',
  export_bicep: 'Export Bicep',
  cost_estimation: 'Estimation de coûts',
  monitoring: 'Monitoring',
  finops_advanced: 'FinOps',
  sso: 'SSO (SAML/OAuth)',
  audit_logs: 'Logs d\'audit',
  api_access: 'Accès API',
  white_label: 'Marque blanche',
  dedicated_support: 'Support dédié',
  priority_support: 'Support prioritaire',
  byoo_azure_devops: 'BYOO Azure DevOps',
  multi_tenant_clients: 'Multi-tenant',
  sla: 'SLA 99.9%',
  custom_integrations: 'Intégrations custom'
};

/**
 * Obtenir les descriptions détaillées des features
 */
export const FEATURE_DESCRIPTIONS: Partial<Record<FeatureName, string>> = {
  multicloud: 'Déployez vos architectures sur Azure, AWS et GCP',
  export_terraform: 'Exportez vos architectures en code Terraform',
  export_bicep: 'Exportez vos architectures en code Bicep (Azure)',
  cost_estimation: 'Estimez les coûts mensuels de vos architectures',
  monitoring: 'Surveillez vos ressources cloud en temps réel',
  finops_advanced: 'Optimisez vos coûts avec analytics et recommandations',
  sso: 'Authentification unique avec SAML ou OAuth',
  audit_logs: 'Traçabilité complète de toutes les actions',
  api_access: 'Intégrez GenIaC à vos outils via API REST',
  white_label: 'Personnalisez GenIaC avec votre marque',
  dedicated_support: 'Support dédié par email',
  priority_support: 'Support prioritaire 24/7 par téléphone',
  byoo_azure_devops: 'Utilisez votre propre organisation Azure DevOps',
  multi_tenant_clients: 'Gérez plusieurs clients (pour ESN/MSP)',
  sla: 'Garantie de disponibilité 99.9%',
  custom_integrations: 'Intégrations sur mesure selon vos besoins'
};
